import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import useDispatch from 'client/hooks/useDispatch';

import {
  UTILITY_TENANT_BULK_IMPORT_SYNC_IMPORT_TYPE_ACTIVE_TENANT_IMPORT,
  UTILITY_TENANT_BULK_IMPORT_SYNC_IMPORT_TYPE_FUTURE_TENANT_IMPORT,
} from 'common/utility/constants';

import { SR_API_ACTION_UTILITY_TENANT_BULK_IMPORT } from 'api/utilityActionTypes';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';

const getFormFields = ({ formValues, utilityPartnerList } = {}) => {
  return [
    {
      title: 'Utility Partner',
      id: 'utilityPartnerId',
      type: 'select',
      properties: {
        placeholder: '--',
        enableResetOption: true,
        choices: (utilityPartnerList || []).map(({ name, id }) => ({
          label: name,
          value: id,
        })),
      },
      validations: ['required'],
    },
    {
      title: 'Import Type',
      id: 'importType',
      type: 'select',
      properties: {
        choices: [
          {
            label: 'Active Tenants',
            value: UTILITY_TENANT_BULK_IMPORT_SYNC_IMPORT_TYPE_ACTIVE_TENANT_IMPORT,
          },
          {
            label: 'Future Tenants',
            value: UTILITY_TENANT_BULK_IMPORT_SYNC_IMPORT_TYPE_FUTURE_TENANT_IMPORT,
          },
        ],
      },
      validations: ['required'],
    },
    showFieldIf(formValues?.utilityPartnerId, {
      id: 'fileUrl',
      title: 'File',
      type: 'file_single',
      properties: {
        fileAccept:
          'text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // eslint-disable-line max-len
        filePath: `/up-uploads/admin/bulk-tenant-import-${formValues?.utilityPartnerId}-{uuid}`,
        isPrivate: true,
      },
      validations: ['required'],
    }),
  ];
};

const UPBulkImportTenantForm = ({ utilityPartnerId, utilityPartnerList, afterSubmit }) => {
  const dispatch = useDispatch();

  const onSubmit = (formValues, formikBag) => {
    const { utilityPartnerId, fileUrl, importType } = formValues;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_TENANT_BULK_IMPORT,
      payload: {
        utilityPartnerId: parseInt(utilityPartnerId, 10),
        importType,
        fileUrl,
      },
      onSuccess() {
        formikBag.resetForm();
        global.alert('File will be processed in the background.');

        if (afterSubmit) {
          afterSubmit();
        }
      },
      onError(error) {
        global.alert(error?.message);
      },
    });
  };

  const initialFormFields = getFormFields({
    utilityPartnerId,
  });

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: initialFormFields,
        defaultValues: {
          utilityPartnerId: null,
          fileUrl: null,
        },
      })}
      enableReinitialize
      validate={createFormValidationFn(initialFormFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formValues = formikBag.values;

        const formFields = getFormFields({
          formValues,
          utilityPartnerList: utilityPartnerList,
        });

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            <SrBox padding="28px 0 12px">
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                disabled={!formikBag.isValid}
                heightType={SR_BUTTON_HEIGHT_MEDIUM}
                hasBoxShadow
                type="submit"
              >
                Submit
              </SrButton>
            </SrBox>
          </Form>
        );
      }}
    />
  );
};

UPBulkImportTenantForm.propTypes = {
  utilityPartnerList: PropTypes.array,
  afterSubmit: PropTypes.func,
};

UPBulkImportTenantForm.displayName = 'UPBulkImportTenantForm';

export default UPBulkImportTenantForm;
